import type { ReactNode } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  LinearProgress,
  Paper,
  Typography,
} from '@mui/material';
import type { DealerLaunchSocketEventLogEntry } from 'app/pages/Header/types/dealerLaunchProgress';
import { formatToDealerTime, parseCalendarDateFromApi } from 'utils/dateUtils';
import {
  computeLaunchActiveDaysProgressPercent,
  computeLaunchChannelProgressPercent,
  getDealerLaunchMaterialization,
  getDealerLaunchMaterializationLabel,
  isDealerLaunchMaterializationFailed,
  isDealerLaunchMaterializationInProgress,
} from 'utils/dealerLaunchMaterialization';

type DealerLaunchProgressModalProps = {
  open: boolean;
  onClose: () => void;
  dealer: unknown;
  latestSocketEvent: DealerLaunchSocketEventLogEntry | null;
  awaitingMonitorRedirect: boolean;
  onRedirectToMonitor: () => void;
};

const DetailRow = ({ label, value }: { label: string; value: ReactNode }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '140px 1fr' },
      gap: 0.5,
      py: 0.75,
      borderBottom: theme => `1px solid ${theme.palette.divider}`,
      '&:last-of-type': { borderBottom: 'none' },
    }}
  >
    <Typography variant="body2" color="text.secondary" fontWeight={600}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
      {value ?? '—'}
    </Typography>
  </Box>
);

const formatStatusLabel = (status?: string | null): string => {
  const raw = String(status ?? '').trim();
  if (!raw) {
    return '—';
  }
  return raw.replace(/_/g, ' ');
};

const statusChipColor = (
  status?: string | null,
): 'default' | 'primary' | 'success' | 'error' | 'warning' => {
  const normalized = String(status ?? '').toLowerCase();
  if (normalized === 'completed' || normalized === 'success') {
    return 'success';
  }
  if (normalized === 'failed' || normalized === 'error') {
    return 'error';
  }
  if (normalized === 'in_progress' || normalized === 'processing' || normalized === 'running') {
    return 'primary';
  }
  return 'default';
};

const formatEventName = (eventName: string): string =>
  eventName.replace(/^dealer_launch_/, '').replace(/_/g, ' ');

const formatPendingLaunchDate = (value?: string): string => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return '—';
  }
  const parsed = parseCalendarDateFromApi(trimmed);
  if (parsed?.isValid) {
    return parsed.toFormat('MM/dd/yyyy');
  }
  return trimmed;
};

const formatLatestSocketEventMessage = (
  entry: DealerLaunchSocketEventLogEntry,
): string => {
  if (entry.eventName === 'dealer_launch_progress') {
    return `Channel ${String(entry.message.channel_id ?? '—')}: ${String(
      entry.message.channels_completed ?? '—',
    )}/${String(entry.message.channels_total ?? '—')} channels, ${String(
      entry.message.active_days_created ?? '—',
    )}/${String(entry.message.active_days_total ?? '—')} active days`;
  }

  if (entry.eventName === 'dealer_launch_completed') {
    const statusText = String(entry.message.status ?? '—');
    const errorText = entry.message.error ? ` — ${String(entry.message.error)}` : '';
    return `Status: ${statusText}${errorText}`;
  }

  const pendingDate = entry.message.pending_launch_date
    ? ` for ${formatPendingLaunchDate(String(entry.message.pending_launch_date))}`
    : '';
  return `Job ${String(entry.message.job_id ?? '—')} received${pendingDate}`;
};

export function DealerLaunchProgressModal({
  open,
  onClose,
  dealer,
  latestSocketEvent,
  awaitingMonitorRedirect,
  onRedirectToMonitor,
}: DealerLaunchProgressModalProps) {
  const materialization = getDealerLaunchMaterialization(dealer);
  const status = materialization?.status ?? null;
  const inProgress = isDealerLaunchMaterializationInProgress(dealer);
  const isFailed = isDealerLaunchMaterializationFailed(dealer);
  const channelPercent = computeLaunchChannelProgressPercent(materialization);
  const activeDaysPercent = computeLaunchActiveDaysProgressPercent(materialization);
  const summaryLabel = getDealerLaunchMaterializationLabel(dealer);

  const currentChannelId =
    latestSocketEvent?.eventName === 'dealer_launch_progress' &&
    latestSocketEvent.message.channel_id
      ? String(latestSocketEvent.message.channel_id)
      : '—';

  const channelsCompleted = materialization?.channels_completed ?? 0;
  const channelsTotal = materialization?.channels_total;
  const activeDaysCreated = materialization?.active_days_created ?? 0;
  const activeDaysTotal = materialization?.active_days_total;

  const showSpinner = inProgress && !awaitingMonitorRedirect;
  const completedLaunchDate =
    latestSocketEvent?.eventName === 'dealer_launch_completed' &&
    latestSocketEvent.message.launch_date
      ? formatPendingLaunchDate(String(latestSocketEvent.message.launch_date))
      : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh' } }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          maxHeight: '85vh',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
            gap: 1,
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Dealer launch progress
          </Typography>
          {awaitingMonitorRedirect ? (
            <Chip size="small" label="Completed" color="success" />
          ) : status ? (
            <Chip
              size="small"
              label={formatStatusLabel(status)}
              color={statusChipColor(status)}
              sx={{ textTransform: 'capitalize' }}
            />
          ) : null}
        </Box>

        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1.5, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            {showSpinner ? <CircularProgress size={16} thickness={5} /> : null}
            <Typography variant="body2" fontWeight={600}>
              {awaitingMonitorRedirect
                ? `Launch completed successfully${
                    completedLaunchDate ? ` — launch date ${completedLaunchDate}` : ''
                  }`
                : summaryLabel}
            </Typography>
          </Box>

          {typeof channelsTotal === 'number' && channelsTotal > 0 ? (
            <Box sx={{ mb: 1.5 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 0.5,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Channels
                </Typography>
                <Typography variant="caption" color="primary.main" fontWeight={600}>
                  {channelsCompleted}/{channelsTotal} ({channelPercent}%)
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={channelPercent}
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Box>
          ) : null}

          {typeof activeDaysTotal === 'number' && activeDaysTotal > 0 ? (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 0.5,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Active days
                </Typography>
                <Typography variant="caption" color="primary.main" fontWeight={600}>
                  {activeDaysCreated}/{activeDaysTotal} ({activeDaysPercent}%)
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={activeDaysPercent}
                color="secondary"
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Box>
          ) : null}
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Launch details
          </Typography>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              border: theme => `1px solid ${theme.palette.divider}`,
            }}
          >
            <DetailRow label="Job ID" value={materialization?.job_id} />
            <DetailRow
              label="Dealer ID"
              value={
                typeof dealer === 'object' && dealer !== null
                  ? String(
                      (dealer as { dealer?: string; _id?: string }).dealer ||
                        (dealer as { _id?: string })._id ||
                        '—',
                    )
                  : '—'
              }
            />
            <DetailRow label="Plan ID" value={materialization?.plan_id} />
            <DetailRow
              label="Pending launch date"
              value={formatPendingLaunchDate(materialization?.pending_launch_date)}
            />
            <DetailRow
              label="Started at"
              value={
                materialization?.started_at
                  ? formatToDealerTime(materialization.started_at)
                  : '—'
              }
            />
            <DetailRow label="Current channel" value={currentChannelId} />
            {isFailed && materialization?.error ? (
              <DetailRow
                label="Error"
                value={
                  <Typography variant="body2" color="error.main">
                    {materialization.error}
                  </Typography>
                }
              />
            ) : null}
          </Box>
        </Box>

        {latestSocketEvent ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Live status
            </Typography>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                border: theme => `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 1,
                  mb: 0.5,
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{ textTransform: 'capitalize' }}
                >
                  {formatEventName(latestSocketEvent.eventName)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatToDealerTime(latestSocketEvent.timestamp)}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {formatLatestSocketEventMessage(latestSocketEvent)}
              </Typography>
            </Box>
          </Box>
        ) : inProgress ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Waiting for socket events...
          </Typography>
        ) : null}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 'auto' }}>
          <Button variant="outlined" size="small" onClick={onClose}>
            Close
          </Button>
          {awaitingMonitorRedirect ? (
            <Button variant="contained" size="small" onClick={onRedirectToMonitor}>
              Go to Monitor
            </Button>
          ) : null}
        </Box>
      </Paper>
    </Dialog>
  );
}
